import React, { useState } from "react";
import client from "../lib/api";
import { Sparkles, Eye, RefreshCw, Lock, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";

/** Premium-only AI questions to ask on the first date for a mutual match.
 *  Renders inside ProfileDetail when there is a mutual match.
 */
const FirstDateQuestions = ({ targetUserId, isPremium, hasMutualMatch }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  if (!hasMutualMatch) return null;

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await client.get(`/first-date-questions/${targetUserId}`);
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Falha ao carregar.");
    } finally { setLoading(false); }
  };

  if (!isPremium) {
    return (
      <section className="bg-gradient-to-br from-[#C5A880]/10 to-[#161920] border border-[#C5A880]/30 rounded-2xl p-6 mt-12"
        data-testid="first-date-locked">
        <div className="flex items-start gap-3 mb-4">
          <Lock className="w-5 h-5 text-[#C5A880] mt-1" />
          <div>
            <span className="label-uppercase">Funcionalidade Premium</span>
            <h2 className="font-display text-2xl mt-1">Perguntas de validação para o 1º encontro</h2>
            <p className="text-sm text-[#DCD7D2] mt-2 max-w-xl">
              A IA gera 6 perguntas personalizadas para fazeres no primeiro encontro,
              com dicas do que observar — pensadas para validar carácter e detectar bandeiras vermelhas.
            </p>
          </div>
        </div>
        <Link to="/premium" className="btn-primary inline-flex items-center gap-2"
          data-testid="first-date-upgrade">
          <Sparkles className="w-4 h-4" /> Desbloquear com Premium
        </Link>
      </section>
    );
  }

  return (
    <section className="mt-12" data-testid="first-date-section">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4 text-[#C5A880]" />
            <span className="label-uppercase">Validação Premium</span>
          </div>
          <h2 className="font-display text-2xl md:text-3xl mt-2">Perguntas para o 1º encontro</h2>
          <p className="text-sm text-[#A29F9B] mt-2 max-w-xl">
            6 perguntas estratégicas adaptadas a estes 2 perfis — para conheceres o carácter, não a fachada.
          </p>
        </div>
        {!data && !loading && (
          <button onClick={load} className="btn-primary inline-flex items-center gap-2"
            data-testid="first-date-load">
            <Sparkles className="w-4 h-4" /> Gerar perguntas
          </button>
        )}
        {data && (
          <button onClick={load} className="btn-secondary inline-flex items-center gap-2 text-xs"
            data-testid="first-date-reload" disabled={loading}>
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Recarregar
          </button>
        )}
      </div>

      {loading && <div className="text-[#A29F9B]">A pensar nas perguntas certas...</div>}

      {err && (
        <div className="bg-[#7A2021]/10 border border-[#7A2021]/40 rounded-2xl p-4 text-sm text-[#E37B7B] inline-flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {err}
        </div>
      )}

      {data && Array.isArray(data.questions) && data.questions.length > 0 && (
        <ol className="space-y-4" data-testid="first-date-list">
          {data.questions.map((q, idx) => (
            <li key={q.question?.slice(0, 80) || `q-${idx}`} className="bg-[#161920] border border-white/5 rounded-2xl p-5"
              data-testid={`first-date-q-${idx}`}>
              <div className="flex items-start gap-4">
                <span className="font-display text-2xl text-[#C5A880] shrink-0">{idx + 1}</span>
                <div>
                  <p className="text-[#F5F2EE] leading-relaxed">{q.question}</p>
                  {q.observe && (
                    <p className="text-xs text-[#A29F9B] italic mt-2 pt-2 border-t border-white/5">
                      <span className="text-[#C5A880] not-italic">O que observar: </span>{q.observe}
                    </p>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}

      {data && (data.questions || []).length === 0 && (
        <p className="text-[#A29F9B] text-sm">Não foi possível gerar perguntas neste momento. Tenta novamente em alguns minutos.</p>
      )}

      <div className="mt-8 p-5 bg-[#161920] border border-white/5 rounded-2xl">
        <p className="text-xs text-[#A29F9B] mb-3 uppercase tracking-wider">Quer mais profundidade?</p>
        <a href="https://ihavethepower.net/loja/coaching/life-coaching-com-coach-ihtp/"
          target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-[#C5A880] hover:underline text-sm"
          data-testid="first-date-coaching">
          → Coaching de preparação para o 1º encontro
        </a>
      </div>
    </section>
  );
};

export default FirstDateQuestions;
